/**
 * The B2B Billing Console (FRD §4-5) - a spreadsheet over one client's rooms.
 *
 * The operator picks a client once and then works down a grid: type the weight,
 * tab, type the rate, next room. Nothing navigates, nothing reloads, and no
 * room opens a page of its own - at 200 rooms every extra interaction is paid
 * two hundred times, which is the whole reason this module exists.
 *
 * This replaces a room-at-a-time console (a room list on the left, one room's
 * form in the middle). The design brief rules that model out explicitly: "Do
 * NOT retain the current design where the left sidebar contains 101 102 103 …
 * and selecting a room opens a separate interface."
 *
 * 5A builds the shell: header, toolbar, selection bar, notice bar, grid chrome.
 * Cell editing, batch save and the unsaved-changes guard land in 5B, which is
 * why Save/Print/Generate are present but inert here rather than absent - the
 * brief fixes their order (Save Changes → Print Tags → Generate Invoice) and
 * moving them later would retrain the operator's hand.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Check, Download, FileText, Printer, Save, Search, X } from 'lucide-react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { useColors } from '../../hooks/useColors'
import { useToast } from '../../contexts/ToastContext'
import { suspendGlobalShortcuts } from '../../hooks/useKeyboardShortcuts'
import EmptyState from '../../components/shared/EmptyState'
import LoadErrorState from '../../components/common/LoadErrorState'
import { withServerTimeout } from '../../components/common/loadErrorUtils'
import { PillButton, SegmentedControl } from '../../features/b2b/components/primitives'
import BillingGrid from '../../features/b2b/components/BillingGrid'
import { useUnsavedChangesGuard } from '../../features/b2b/useUnsavedChangesGuard'
import {
  entryHasBlockingErrors,
  entryToPayload,
  filterRowsWithDrafts,
  roomEntryFromRow,
  rowsFromCycle,
  totalsWithDrafts,
} from '../../features/b2b/components/billingRowModel'
import type { BillingRowStatus, RoomEntry } from '../../features/b2b/components/billingRowModel'
import type { BillingField } from '../../features/b2b/components/BillingRow'
import ItemLinesModal from '../../features/b2b/components/ItemLinesModal'
import RowActionsMenu from '../../features/b2b/components/RowActionsMenu'
import RoomServiceModal from '../../features/b2b/components/RoomServiceModal'
import PrintTagsModal from '../../features/b2b/print/PrintTagsModal'
import { openRoomSlip } from '../../features/b2b/print/b2bRoomDocs'
import type { RoomSlipOrder } from '../../features/b2b/print/b2bRoomDocs'
import type { PrintScope } from '../../features/b2b/print/printScope'
import type { DraftItemLine } from '../../features/b2b/draft'
import { formatMoney } from '../../features/b2b/format'
import { b2bErrorMessage } from '../../features/b2b/errorCopy'
import { b2bApi } from '../../services/b2bApi'
import type { B2bCycleResponse, B2bOrderRecord } from '../../services/b2bApi'

type StatusFilter = 'all' | BillingRowStatus

const STATUS_FILTERS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'done', label: 'Done' },
]

/**
 * Unsaved changes → saving → "✓ All changes saved" (brief §29).
 *
 * Subtle on purpose: the operator needs to know whether their work is safe,
 * without a banner competing with the numbers they are reading.
 */
function SaveState({
  dirty,
  rooms,
  saving,
  justSaved,
}: {
  dirty: boolean
  rooms: number
  saving: boolean
  justSaved: boolean
}) {
  const colors = useColors()

  if (saving) {
    return (
      <span className="font-body" data-testid="save-state" style={{ fontSize: '11px', color: colors.textMuted }}>
        Saving…
      </span>
    )
  }
  if (dirty) {
    return (
      <span
        className="font-body"
        data-testid="save-state"
        style={{ fontSize: '11px', fontWeight: 600, color: 'var(--chip-warning-ink)' }}
      >
        Unsaved changes ({rooms} room{rooms === 1 ? '' : 's'})
      </span>
    )
  }
  if (justSaved) {
    return (
      <span
        className="font-body"
        data-testid="save-state"
        style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 600, color: 'var(--chip-success-ink)' }}
      >
        <Check size={13} /> All changes saved
      </span>
    )
  }
  return null
}

/** One of the header's three right-aligned figures. */
function HeaderStat({
  label,
  value,
  suffix,
  color,
  divider,
}: {
  label: string
  value: string
  suffix?: string
  color?: string
  divider?: boolean
}) {
  const colors = useColors()

  return (
    <div
      style={{
        textAlign: 'right',
        paddingLeft: divider ? '20px' : undefined,
        borderLeft: divider ? `1px solid ${colors.border}` : undefined,
      }}
    >
      <div
        className="font-body"
        style={{
          fontSize: '10px',
          fontWeight: 700,
          letterSpacing: '0.9px',
          textTransform: 'uppercase',
          color: colors.textMuted,
        }}
      >
        {label}
      </div>
      <div
        className="font-body"
        style={{ marginTop: '3px', fontSize: '18px', fontWeight: 700, color: color ?? colors.textPrimary }}
      >
        {value}
        {suffix ? (
          <span style={{ fontSize: '12px', fontWeight: 500, color: colors.textSecondary }}> {suffix}</span>
        ) : null}
      </div>
    </div>
  )
}

export default function B2bBillingConsolePage() {
  const colors = useColors()
  const navigate = useNavigate()
  const { addToast } = useToast()
  const [searchParams] = useSearchParams()
  const clientId = searchParams.get('client')

  const [cycle, setCycle] = useState<B2bCycleResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [query, setQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set())
  const [notice, setNotice] = useState('')

  /**
   * Unsaved edits, keyed by room.
   *
   * Held at PAGE level, not inside each row, because the footer totals have to
   * move on every keystroke - a running total that only caught up on blur would
   * be wrong for most of the time an operator is looking at it. Keeping one row
   * cheap is handled by identity instead: editing room X replaces only X's entry,
   * so React.memo skips the other 199 rows even though the page re-rendered.
   */
  const [drafts, setDrafts] = useState<ReadonlyMap<string, RoomEntry>>(new Map())
  const [showErrors, setShowErrors] = useState(false)

  /** Which room's whole-room editor is open, and which item editor above it. */
  const [openRoomId, setOpenRoomId] = useState<string | null>(null)
  const [itemEditor, setItemEditor] = useState<{ roomId: string; serviceSlug: string } | null>(null)
  const [rowMenu, setRowMenu] = useState<{ roomId: string; anchor: DOMRect } | null>(null)
  const [saving, setSaving] = useState(false)
  const [justSaved, setJustSaved] = useState(false)

  /**
   * The open Print Tags dialog, and what it opened on.
   *
   * The scope radio group is the operator's, but where they came FROM sets its
   * starting position: the row menu means one room, the toolbar means whatever
   * is ticked. Carrying its own room set (rather than reading the grid
   * selection) is what lets the row menu scope to a single room without
   * disturbing a selection the operator built for something else.
   */
  const [tagPrint, setTagPrint] = useState<{ scope: PrintScope; roomIds: ReadonlySet<string> } | null>(
    null,
  )

  // Bare-letter global nav ("o" → Orders) would fire on the first keystroke
  // into a grid cell. Suspended for as long as the console is mounted.
  useEffect(() => suspendGlobalShortcuts(), [])

  const load = useCallback(async () => {
    if (!clientId) {
      setLoading(false)
      return
    }
    setLoading(true)
    setError('')
    try {
      const data = await withServerTimeout(b2bApi.getOpenCycle(clientId))
      setCycle(data)
    } catch (err) {
      setError(b2bErrorMessage(err, 'loading this billing cycle'))
    } finally {
      setLoading(false)
    }
  }, [clientId])

  useEffect(() => {
    void load()
  }, [load])

  const rows = useMemo(() => (cycle ? rowsFromCycle(cycle) : []), [cycle])
  const rowById = useMemo(() => new Map(rows.map((row) => [row.roomId, row])), [rows])
  const visible = useMemo(
    () => filterRowsWithDrafts(rows, drafts, query, statusFilter),
    [rows, drafts, query, statusFilter],
  )
  const totals = useMemo(() => totalsWithDrafts(visible, drafts), [visible, drafts])

  const dirty = drafts.size > 0
  const invalidRooms = useMemo(
    () => [...drafts.entries()].filter(([, entry]) => entryHasBlockingErrors(entry)).map(([roomId]) => roomId),
    [drafts],
  )

  /**
   * Record a keystroke.
   *
   * A room's first edit seeds its draft from whatever is already saved, so
   * correcting a weight never silently drops the rate beside it. Values are
   * stored as the operator typed them - "3." stays "3." until they finish.
   */
  const entryFor = useCallback(
    (roomId: string): RoomEntry => {
      const existing = drafts.get(roomId)
      if (existing) return existing
      const row = rowById.get(roomId)
      return row
        ? roomEntryFromRow(row, cycle?.rate_card.washIronPerKg ?? null)
        : { weight: { id: `draft-${roomId}`, serviceSlug: 'wash-and-iron', weightKg: '', ratePerKg: '', pieces: '', items: [] }, items: {} }
    },
    [cycle?.rate_card.washIronPerKg, drafts, rowById],
  )

  const handleEdit = useCallback(
    (roomId: string, field: BillingField, value: string) => {
      setDrafts((current) => {
        const next = new Map(current)
        const base = current.get(roomId) ?? entryFor(roomId)
        next.set(roomId, { ...base, weight: { ...base.weight, [field]: value } })
        return next
      })
    },
    [entryFor],
  )

  /** Garment lines coming back from the item editor. */
  const handleApplyItems = useCallback(
    (roomId: string, serviceSlug: string, lines: DraftItemLine[]) => {
      setDrafts((current) => {
        const next = new Map(current)
        const base = current.get(roomId) ?? entryFor(roomId)
        next.set(roomId, { ...base, items: { ...base.items, [serviceSlug]: lines } })
        return next
      })
      setItemEditor(null)
    },
    [entryFor],
  )

  const handleRemoveService = useCallback(
    (roomId: string, serviceSlug: string) => {
      setDrafts((current) => {
        const next = new Map(current)
        const base = current.get(roomId) ?? entryFor(roomId)
        if (serviceSlug === 'wash-and-iron') {
          next.set(roomId, { ...base, weight: { ...base.weight, weightKg: '', pieces: '' } })
        } else {
          next.set(roomId, { ...base, items: { ...base.items, [serviceSlug]: [] } })
        }
        return next
      })
    },
    [entryFor],
  )

  /**
   * Save every dirty room in ONE request.
   *
   * Optimistic: the rows flip to saved immediately, because an operator who has
   * just pressed Save should see it land, not watch a spinner over their work.
   * If the server refuses, the drafts go straight back - the design's own
   * failure wording is "Room 104 could not be saved. Your entered values are
   * still here," and that has to be literally true.
   */
  const handleSave = useCallback(async () => {
    setShowErrors(true)
    if (drafts.size === 0 || saving || !cycle) return

    if (invalidRooms.length > 0) {
      const first = rowById.get(invalidRooms[0])
      addToast(
        invalidRooms.length === 1
          ? `Room ${first?.roomNumber ?? ''} needs a weight and a rate above 0.`
          : `${invalidRooms.length} rooms need a weight and a rate above 0.`,
        'error',
      )
      return
    }

    const pending = [...drafts.entries()]
    const rollback = new Map(drafts)

    setSaving(true)
    setDrafts(new Map())

    try {
      const result = await b2bApi.saveCycleOrders(
        cycle.cycle.id,
        pending.map(([roomId, entry]) => ({ roomId, orders: entryToPayload(entry) })),
      )

      // Reconcile from the response rather than assuming: the server recomputes
      // every amount, so its numbers are the ones that must end up on screen.
      setCycle((current) => {
        if (!current) return current
        const touched = new Set(result.rooms.map((roomState) => roomState.roomId))
        return {
          ...current,
          orders: [
            ...current.orders.filter((order) => !touched.has(order.roomId)),
            ...result.rooms.flatMap((roomState) => roomState.orders),
          ],
          summary: result.summary,
        }
      })

      setShowErrors(false)
      setJustSaved(true)
      addToast(
        pending.length === 1
          ? `Room ${rowById.get(pending[0][0])?.roomNumber ?? ''} saved.`
          : `${pending.length} rooms saved.`,
        'success',
      )
    } catch (err) {
      // Put the operator's work back exactly as it was.
      setDrafts(rollback)
      addToast(b2bErrorMessage(err, 'saving these rooms'), 'error')
    } finally {
      setSaving(false)
    }
  }, [addToast, cycle, drafts, invalidRooms, rowById, saving])

  // The "✓ All changes saved" state is a moment, not a mode.
  useEffect(() => {
    if (!justSaved) return undefined
    const timer = window.setTimeout(() => setJustSaved(false), 4000)
    return () => window.clearTimeout(timer)
  }, [justSaved])

  useUnsavedChangesGuard(
    drafts.size > 0,
    'You have unsaved room billing. Leave this page and those entries are lost.',
  )

  // Selecting "all" means all VISIBLE rows - never rooms hidden behind a filter.
  const allSelected = visible.length > 0 && visible.every((row) => selected.has(row.roomId))
  // Some-but-not-all, counted over the VISIBLE rows only - a tick that reflected
  // rooms hidden behind a filter would be lying about what it will act on.
  const someSelected = !allSelected && visible.some((row) => selected.has(row.roomId))

  /**
   * Selected rooms the current filter is hiding.
   *
   * Selection deliberately survives a filter change - the operator chose those
   * rooms. But a bar reading "12 rooms selected" over ten visible rows, with a
   * bulk action that would touch all twelve, is the kind of silent surprise
   * this module exists to avoid. So say it.
   */
  const hiddenSelectedCount = useMemo(() => {
    const visibleIds = new Set(visible.map((row) => row.roomId))
    return [...selected].filter((roomId) => !visibleIds.has(roomId)).length
  }, [selected, visible])

  const toggleAll = useCallback(() => {
    setSelected((current) => {
      const next = new Set(current)
      if (visible.every((row) => next.has(row.roomId))) {
        visible.forEach((row) => next.delete(row.roomId))
      } else {
        visible.forEach((row) => next.add(row.roomId))
      }
      return next
    })
  }, [visible])

  const toggleSelect = useCallback((roomId: string) => {
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(roomId)) next.delete(roomId)
      else next.add(roomId)
      return next
    })
  }, [])

  /** Escape in a cell - put back what the server last gave us for that field. */
  const revertCell = useCallback(
    (roomId: string | undefined, column: string) => {
      if (!roomId) return
      const row = rowById.get(roomId)
      if (!row) return
      const field: BillingField =
        column === 'rate' ? 'ratePerKg' : column === 'pieces' ? 'pieces' : 'weightKg'
      const saved =
        field === 'ratePerKg'
          ? row.ratePerKg
          : field === 'pieces'
            ? row.pieces
            : row.weightKg

      setDrafts((current) => {
        const existing = current.get(roomId)
        if (!existing) return current
        const next = new Map(current)
        const restored = {
          ...existing,
          weight: { ...existing.weight, [field]: saved === null || saved === 0 ? '' : String(saved) },
        }
        // A room with nothing left to say is no longer dirty.
        const untouched =
          !restored.weight.weightKg.trim() &&
          !restored.weight.pieces.trim() &&
          Object.values(restored.items).every((lines) => lines.length === 0)
        if (untouched && row.total === 0) next.delete(roomId)
        else next.set(roomId, restored)
        return next
      })
    },
    [rowById],
  )

  /**
   * Record a finished print run.
   *
   * Optimistic, and deliberately forgiving: the tags are already on paper by the
   * time this runs, so a failed stamp must not read as a failed print. It costs
   * the operator the marker until the next reload - annoying - where a scary
   * error would cost them a second run of 134 tags they do not need.
   */
  const handlePrinted = useCallback(
    async (orderIds: string[]) => {
      if (orderIds.length === 0) return
      const printedAt = new Date().toISOString()
      const stamped = new Set(orderIds)

      setCycle((current) =>
        current
          ? {
              ...current,
              orders: current.orders.map((order) =>
                stamped.has(order.id) ? { ...order, printedAt } : order,
              ),
            }
          : current,
      )

      try {
        await b2bApi.markTagsPrinted(orderIds)
      } catch {
        addToast('Tags printed, but the printed marker could not be saved.', 'info')
      }
    },
    [addToast],
  )

  /** Open Print Tags for exactly one room - the row menu and the room editor. */
  const printRoomTags = useCallback(
    (roomId: string) => setTagPrint({ scope: 'selected', roomIds: new Set([roomId]) }),
    [],
  )

  /** Open Print Tags for the toolbar: the ticked rooms, or everything billed. */
  const printCycleTags = useCallback(
    () =>
      setTagPrint({
        scope: selected.size > 0 ? 'selected' : 'completed',
        roomIds: selected,
      }),
    [selected],
  )

  /**
   * The room's A5 slip - the sorting sheet, never an invoice (FRD §8).
   * Straight to the printer: it is one page describing one room, and the grid
   * beside it already shows every figure on it.
   */
  const printRoomSlipFor = useCallback(
    (roomId: string) => {
      if (!cycle) return
      const row = rowById.get(roomId)
      const roomOrders: B2bOrderRecord[] = cycle.orders.filter((order) => order.roomId === roomId)
      if (!row || roomOrders.length === 0) return

      openRoomSlip(
        {
          clientName: cycle.client.name,
          roomNumber: row.roomNumber,
          block: row.block,
          date: roomOrders.reduce(
            (latest, order) => (order.createdAt > latest ? order.createdAt : latest),
            roomOrders[0].createdAt,
          ),
        },
        roomOrders as RoomSlipOrder[],
      )
    },
    [cycle, rowById],
  )

  const notYet = useCallback(
    (what: string) => addToast(`${what} arrives with the next step.`, 'info'),
    [addToast],
  )
  const openRoom = useCallback((roomId: string) => setOpenRoomId(roomId), [])
  const openItems = useCallback(
    (roomId: string, serviceSlug: string) => setItemEditor({ roomId, serviceSlug }),
    [],
  )
  const openMenu = useCallback(
    (roomId: string, anchor: DOMRect) => setRowMenu({ roomId, anchor }),
    [],
  )

  if (!clientId) {
    return (
      <div>
        <EmptyState
          title="Pick a client to start billing"
          description="Open a client from the B2B client master to load its rooms for this cycle."
        />
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <PillButton onClick={() => navigate('/b2b/clients')}>Go to B2B clients</PillButton>
        </div>
      </div>
    )
  }

  if (error && !loading) {
    return <LoadErrorState message={error} onRetry={() => void load()} />
  }

  const summary = cycle?.summary
  const roomsTotal = summary?.rooms_total ?? 0
  const roomsBilled = summary?.rooms_billed ?? 0
  const roomsPending = summary?.rooms_pending ?? 0

  return (
    // A column that fills the page so the grid - not the window - owns the
    // scroll, which is what lets its head and footer pin.
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 150px)', minHeight: '520px' }}>
      {/* ── header ─────────────────────────────────────────────── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: '16px',
          flexWrap: 'wrap',
          marginBottom: '14px',
        }}
      >
        <div>
          <button
            type="button"
            className="font-body"
            onClick={() => navigate('/b2b/clients')}
            style={{
              border: 'none',
              background: 'transparent',
              padding: 0,
              fontSize: '11px',
              fontWeight: 600,
              color: colors.textSecondary,
              cursor: 'pointer',
            }}
          >
            B2B Billing
          </button>
          <h1
            className="font-display"
            style={{ margin: '2px 0 0', fontSize: '24px', fontWeight: 600, letterSpacing: '-0.3px' }}
          >
            {cycle?.client.name ?? 'Billing console'}
          </h1>
          <p className="font-body" style={{ margin: '2px 0 0', fontSize: '12px', color: colors.textSecondary }}>
            {cycle ? `${cycle.cycle.label} billing cycle` : 'Loading cycle…'}
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '20px' }}>
          <HeaderStat label="Rooms billed" value={String(roomsBilled)} suffix={`/ ${roomsTotal}`} />
          <HeaderStat label="Pending rooms" value={String(roomsPending)} color="var(--chip-warning-ink)" divider />
          {/* Straight from the server summary - the design prototype adds a
              hard-coded ₹40,180 to this figure, which would overstate every
              client's bill. */}
          <HeaderStat
            label="Client running total"
            value={formatMoney(summary?.total_amount ?? 0)}
            divider
          />
        </div>
      </div>

      {/* ── toolbar ────────────────────────────────────────────── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          flexWrap: 'wrap',
          padding: '10px 12px',
          border: `1px solid ${colors.border}`,
          borderBottom: 'none',
          borderRadius: '12px 12px 0 0',
          background: colors.bgCard,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              height: '34px',
              width: '210px',
              padding: '0 10px',
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              background: colors.bgCard,
            }}
          >
            <Search size={15} color={colors.textMuted} />
            <input
              value={query}
              aria-label="Search rooms"
              placeholder="Search rooms"
              onChange={(event) => setQuery(event.target.value)}
              className="font-body"
              style={{
                flex: 1,
                minWidth: 0,
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: '12px',
                color: colors.textPrimary,
              }}
            />
          </label>

          <SegmentedControl
            size="sm"
            value={statusFilter}
            options={STATUS_FILTERS}
            onChange={setStatusFilter}
          />
        </div>

        {/* Priority order is fixed by the brief: Save, then Print, then
            Generate - Export CSV is secondary. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          {/* The save state, stated rather than implied (brief §29). */}
          <SaveState dirty={dirty} rooms={drafts.size} saving={saving} justSaved={justSaved} />

          <PillButton variant="secondary" onClick={() => notYet('CSV export')}>
            <Download size={14} /> Export CSV
          </PillButton>
          {/* Tags and the invoice describe what is on the SERVER. Offering them
              over unsaved edits would print a bag list that does not match the
              bill. The design says the same: "Save before printing tags or
              generating the client invoice." */}
          <PillButton
            variant="secondary"
            disabled={dirty || (summary?.rooms_billed ?? 0) === 0}
            title={
              dirty
                ? 'Save your changes before printing tags'
                : (summary?.rooms_billed ?? 0) === 0
                  ? 'Bill a room first'
                  : undefined
            }
            onClick={printCycleTags}
          >
            <Printer size={14} /> Print tags
          </PillButton>
          <PillButton
            variant="outline"
            disabled={dirty || (summary?.rooms_billed ?? 0) === 0}
            title={
              dirty
                ? 'Save your changes before generating the invoice'
                : (summary?.rooms_billed ?? 0) === 0
                  ? 'Bill a room first'
                  : undefined
            }
            onClick={() => notYet('Invoice generation')}
          >
            <FileText size={14} /> Generate invoice
          </PillButton>
          <PillButton
            onClick={() => void handleSave()}
            disabled={!dirty || saving}
            title={dirty ? undefined : 'No unsaved changes'}
          >
            <Save size={14} /> {saving ? 'Saving…' : dirty ? `Save changes (${drafts.size})` : 'Save changes'}
          </PillButton>
        </div>
      </div>

      {/* ── selection bar ──────────────────────────────────────── */}
      {selected.size > 0 ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            padding: '8px 12px',
            borderLeft: `1px solid ${colors.border}`,
            borderRight: `1px solid ${colors.border}`,
            background: 'rgba(214,185,123,0.14)',
          }}
        >
          <span className="font-body" style={{ fontSize: '12px', fontWeight: 700 }}>
            {selected.size} room{selected.size === 1 ? '' : 's'} selected
            {hiddenSelectedCount > 0 ? (
              <span style={{ fontWeight: 500, color: colors.textSecondary }}>
                {' '}
                ({hiddenSelectedCount} hidden by this filter)
              </span>
            ) : null}
          </span>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <button
              type="button"
              className="font-body"
              onClick={() => setTagPrint({ scope: 'selected', roomIds: selected })}
              style={{
                border: 'none',
                background: 'transparent',
                fontSize: '11px',
                fontWeight: 700,
                color: colors.accentText,
                cursor: 'pointer',
              }}
            >
              Print tags
            </button>
            {/* The consolidated CLIENT invoice - the only invoice there is
                (FRD §8). Selecting rooms narrows what you are looking at, not
                who is billed. */}
            <button
              type="button"
              className="font-body"
              onClick={() => notYet('The client invoice review')}
              style={{
                border: 'none',
                background: 'transparent',
                fontSize: '11px',
                fontWeight: 700,
                color: colors.accentText,
                cursor: 'pointer',
              }}
            >
              Review client invoice
            </button>
            <button
              type="button"
              className="font-body"
              onClick={() => setSelected(new Set())}
              style={{
                border: 'none',
                background: 'transparent',
                fontSize: '11px',
                fontWeight: 600,
                color: colors.textSecondary,
                cursor: 'pointer',
              }}
            >
              Clear
            </button>
          </div>
        </div>
      ) : null}

      {/* ── notice bar ─────────────────────────────────────────── */}
      {notice ? (
        <div
          role="status"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 12px',
            borderLeft: `1px solid ${colors.border}`,
            borderRight: `1px solid ${colors.border}`,
            background: 'rgba(21,128,61,0.10)',
            color: 'var(--chip-success-ink)',
            fontSize: '12px',
            fontWeight: 600,
          }}
        >
          <span>{notice}</span>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => setNotice('')}
            style={{ border: 'none', background: 'transparent', color: 'inherit', cursor: 'pointer' }}
          >
            <X size={14} />
          </button>
        </div>
      ) : null}

      {/* ── grid ───────────────────────────────────────────────── */}
      <BillingGrid
        rows={visible}
        totals={totals}
        drafts={drafts}
        showErrors={showErrors}
        onEdit={handleEdit}
        selected={selected}
        allSelected={allSelected}
        someSelected={someSelected}
        onToggleAll={toggleAll}
        onToggleSelect={toggleSelect}
        onOpenRoom={openRoom}
        onOpenItems={openItems}
        onOpenMenu={openMenu}
        onRevertCell={revertCell}
        placeholder={
          loading ? (
            <div style={{ padding: '16px', display: 'grid', gap: '8px' }}>
              {Array.from({ length: 8 }).map((_, index) => (
                <div
                  key={index}
                  className="rapidry-pulse"
                  style={{ height: '32px', borderRadius: '8px', background: colors.surface }}
                />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              title="No rooms added yet"
              description="Add this client's rooms before billing - they are created once and reused every cycle."
            />
          ) : visible.length === 0 ? (
            <EmptyState
              title="No rooms match"
              description="Clear the search or choose a different status to see the rest of this cycle."
            />
          ) : undefined
        }
      />

      {/* The whole-room view behind the Room cell. */}
      {openRoomId ? (
        <RoomServiceModal
          roomNumber={rowById.get(openRoomId)?.roomNumber ?? ''}
          entry={entryFor(openRoomId)}
          savedDcAmount={rowById.get(openRoomId)?.dcAmount ?? 0}
          savedShoeAmount={rowById.get(openRoomId)?.shoeAmount ?? 0}
          savedDcCount={rowById.get(openRoomId)?.dcItemCount ?? 0}
          savedShoeCount={rowById.get(openRoomId)?.shoeItemCount ?? 0}
          onChangeWeight={(field, value) => handleEdit(openRoomId, field, value)}
          onEditItems={(serviceSlug) => setItemEditor({ roomId: openRoomId, serviceSlug })}
          onRemoveService={(serviceSlug) => handleRemoveService(openRoomId, serviceSlug)}
          onClose={() => setOpenRoomId(null)}
          onSave={() => {
            setOpenRoomId(null)
            handleSave()
          }}
          // Tags describe what is on the server, so an unsaved room cannot print.
          canPrint={!drafts.has(openRoomId) && (rowById.get(openRoomId)?.total ?? 0) > 0}
          onPrintTags={() => {
            const roomId = openRoomId
            setOpenRoomId(null)
            printRoomTags(roomId)
          }}
        />
      ) : null}

      {rowMenu ? (
        <RowActionsMenu
          roomNumber={rowById.get(rowMenu.roomId)?.roomNumber ?? ''}
          anchor={rowMenu.anchor}
          canPrintTags={!drafts.has(rowMenu.roomId) && (rowById.get(rowMenu.roomId)?.total ?? 0) > 0}
          onClose={() => setRowMenu(null)}
          onEditBilling={() => setOpenRoomId(rowMenu.roomId)}
          onViewServices={() => setOpenRoomId(rowMenu.roomId)}
          onPrintTags={() => printRoomTags(rowMenu.roomId)}
          onPrintRoomSlip={() => printRoomSlipFor(rowMenu.roomId)}
        />
      ) : null}

      {/* Print tags: scope, services, copies, preview and the §28 guard. */}
      {tagPrint && cycle ? (
        <PrintTagsModal
          clientName={cycle.client.name}
          cycleLabel={cycle.cycle.label}
          rooms={cycle.rooms}
          orders={cycle.orders}
          selectedRoomIds={tagPrint.roomIds}
          catalogue={cycle.catalogue}
          initialScope={tagPrint.scope}
          onClose={() => setTagPrint(null)}
          onPrinted={(orderIds) => void handlePrinted(orderIds)}
        />
      ) : null}

      {/* Garment pricing has one implementation, opened from either surface. */}
      {itemEditor && cycle ? (
        <ItemLinesModal
          roomNumber={rowById.get(itemEditor.roomId)?.roomNumber ?? ''}
          serviceSlug={itemEditor.serviceSlug}
          lines={entryFor(itemEditor.roomId).items[itemEditor.serviceSlug] ?? []}
          rateCard={cycle.rate_card}
          onCancel={() => setItemEditor(null)}
          onApply={(lines) => handleApplyItems(itemEditor.roomId, itemEditor.serviceSlug, lines)}
        />
      ) : null}
    </div>
  )
}
